import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Issue } from '@/types';

interface CalendarEventItem {
  issueId: string;
  issueSequence: number;
  issueDescription: string;
  eventType: 'task' | 'st-breakpoint' | 'lt-breakpoint';
  eventDate: string;
  owner: string;
}

interface IssuesCalendarGridProps {
  productIssues: Issue[];
  productionIssues: Issue[];
  selectedDate: string;
  onDateSelect: (date: string) => void;
}

const formatDateKey = (year: number, month: number, day: number): string => {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
};

export function IssuesCalendarGrid({ productIssues, productionIssues, selectedDate, onDateSelect }: IssuesCalendarGridProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const allIssues = [...productIssues, ...productionIssues];

  const eventsByDate = useMemo(() => {
    const events = new Map<string, CalendarEventItem[]>();

    allIssues.forEach(issue => {
      const shortTermAction = issue.actionItems?.find(a => a.actionType === 'short_term');
      const longTermAction = issue.actionItems?.find(a => a.actionType === 'long_term');
      const shortTermCompleted = shortTermAction?.status === 'completed' || shortTermAction?.status === 'delayed-complete';
      const longTermCompleted = longTermAction?.status === 'completed' || longTermAction?.status === 'delayed-complete';

      issue.planTasks.forEach(task => {
        const taskCompleted = task.status === 'completed' || task.status === 'delayed-completed';
        const eventDate = task.newPlannedDate || task.plannedDate;
        if (eventDate && !taskCompleted) {
          const dateKey = eventDate;
          if (!events.has(dateKey)) {
            events.set(dateKey, []);
          }
          events.get(dateKey)!.push({
            issueId: issue.id,
            issueSequence: issue.sequence,
            issueDescription: issue.description,
            eventType: 'task',
            eventDate: eventDate,
            owner: issue.owner || 'Unknown',
          });
        }
      });

      issue.actionItems?.forEach(action => {
        const isBpNg = action.status === 'bp-ng';
        const isDelayedComplete = action.status === 'delayed-complete';
        const isCompleted = action.status === 'completed';
        
        if (isCompleted || isDelayedComplete) return;
        
        let effectiveDate = action.breakpointDate;
        
        if (isBpNg && action.delayHistory && action.delayHistory.length > 0) {
          const latestDelay = action.delayHistory[action.delayHistory.length - 1];
          effectiveDate = latestDelay.newPlannedDate;
        }
        
        if (effectiveDate) {
          const dateKey = effectiveDate;
          if (!events.has(dateKey)) {
            events.set(dateKey, []);
          }
          events.get(dateKey)!.push({
            issueId: issue.id,
            issueSequence: issue.sequence,
            issueDescription: issue.description,
            eventType: action.actionType === 'short_term' ? 'st-breakpoint' : 'lt-breakpoint',
            eventDate: effectiveDate,
            owner: issue.owner || 'Unknown',
          });
        }
      });
    });

    return events;
  }, [allIssues]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const startingDayOfWeek = firstDayOfMonth.getDay();
    const totalDaysInMonth = lastDayOfMonth.getDate();

    const days: (number | null)[] = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= totalDaysInMonth; day++) {
      days.push(day);
    }

    return days;
  }, [currentMonth]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['S', 'S', 'M', 'T', 'W', 'T', 'F'];

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const handleDateClick = (day: number) => {
    const dateStr = formatDateKey(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onDateSelect(dateStr);
  };

  const getEventCount = (day: number): number => {
    const dateStr = formatDateKey(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const events = eventsByDate.get(dateStr) || [];
    return events.length;
  };

  const hasEvents = (day: number): boolean => {
    return getEventCount(day) > 0;
  };

  return (
    <div className="p-2 rounded-xl border border-gray-100 hover:shadow-md transition-shadow bg-white h-[300px] flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <button
            onClick={goToPreviousMonth}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <span className="text-sm font-bold text-gray-800 min-w-[140px] text-center">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <button
            onClick={goToNextMonth}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <button
          onClick={goToToday}
          className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
        >
          Today
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0 mb-0">
        {dayNames.map((day, index) => (
          <div
            key={index}
            className="text-center text-xs font-bold text-gray-700 py-0"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0 flex-1 overflow-hidden">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return (
              <div
                key={`empty-${index}`}
                className="h-8 bg-transparent"
              />
            );
          }

          const eventCount = getEventCount(day);
          const hasEvent = hasEvents(day);
          const isCurrentDay = isToday(day);
          const isSelected = selectedDate === formatDateKey(currentMonth.getFullYear(), currentMonth.getMonth(), day);

          return (
            <div
              key={day}
              onClick={() => handleDateClick(day)}
              className={`
                h-8 p-0.5 rounded cursor-pointer transition-all
                flex flex-col items-center justify-center relative
                ${isSelected ? 'bg-blue-100' : hasEvent ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'}
              `}
            >
              <span
                className={`
                  text-xs font-medium text-black
                  ${isCurrentDay ? 'font-bold' : ''}
                `}
              >
                {day}
              </span>

              {hasEvent && (
                <div
                  className="absolute bottom-0.5 right-0.5 rounded-full bg-blue-500 flex items-center justify-center"
                  style={{
                    width: '12px',
                    height: '12px',
                    fontSize: '6px',
                  }}
                >
                  <span className="text-white font-bold text-[6px] leading-none">
                    {eventCount}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
